import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, MessageCircle } from "lucide-react";
import DOMPurify from "dompurify";
import { api, WHATSAPP_LINK } from "@/lib/api";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return "";
  }
}

function isHtmlContent(content) {
  return /<\/?[a-z][\s\S]*>/i.test(content || "");
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/posts/${slug}`)
      .then((res) => setPost(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div data-testid="blogpost-loading" className="container-narrow section">
        <p className="text-[#5B685C] font-light">Carregando...</p>
      </div>
    );
  }
  if (notFound || !post) {
    return (
      <div data-testid="blogpost-notfound" className="container-narrow section">
        <h1 className="font-serif text-4xl text-[#2C352D]">Artigo não encontrado</h1>
        <Link to="/blog" className="btn-outline mt-8 inline-flex">
          <ArrowLeft size={16} /> Voltar aos artigos
        </Link>
      </div>
    );
  }

  return (
    <article data-testid={`blogpost-${post.slug}`} className="bg-[#FAF9F6]">
      <header className="container-narrow pt-16 pb-12">
        <Link to="/blog" data-testid="blogpost-back" className="inline-flex items-center gap-2 text-[#5B685C] hover:text-[#2C352D]">
          <ArrowLeft size={16} /> Todos os artigos
        </Link>
        <div className="max-w-3xl mt-10">
          <div className="flex items-center gap-2 text-eyebrow text-[#8BA88E]">
            <Calendar size={12} /> {formatDate(post.created_at)}
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl md:text-6xl mt-4 text-[#2C352D] leading-[1.1]">
            {post.title}
          </h1>
          <p className="mt-6 text-[#5B685C] text-lg leading-relaxed font-light">{post.excerpt}</p>
        </div>
      </header>

      {post.cover_image && (
        <div className="container-narrow">
          <img
            src={post.cover_image.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${post.cover_image}` : post.cover_image}
            alt={post.title}
            className="w-full h-[280px] md:h-[480px] object-cover rounded-[2rem] soft-shadow"
            data-testid="blogpost-cover"
          />
        </div>
      )}

      <div className="container-narrow max-w-3xl pt-12 pb-24">
        <div className="prose-soft rich-content">
          {isHtmlContent(post.content) ? (
            <div
              data-testid="blogpost-html-content"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(post.content.replace(/&nbsp;|\u00a0/g, " ")),
              }}
            />
          ) : (
            post.content.split(/\n\n+/).map((para, i) => <p key={i}>{para}</p>)
          )}
        </div>

        <div className="mt-16 bg-[#F5F5ED] rounded-3xl p-10 border border-[#8BA88E]/15">
          <h3 className="font-serif text-2xl text-[#2C352D]">Conversar pode ser o início de um novo caminho.</h3>
          <p className="mt-3 text-[#5B685C] font-light">
            Se algo neste texto te tocou, considere agendar uma conversa inicial.
          </p>
          <a
            data-testid="blogpost-cta"
            href={WHATSAPP_LINK("Olá Cristina, li um artigo no seu site e gostaria de conversar.")}
            target="_blank"
            rel="noreferrer"
            className="btn-whatsapp mt-6"
          >
            <MessageCircle size={18} /> Agendar pelo WhatsApp
          </a>
        </div>
      </div>
    </article>
  );
}
